import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import type { CourtResponse } from "@bw/shared/types/generated";

import { StatusBadge } from "@/components/StatusBadge";

import { formatCourtTags, type FacilityRead } from "./facilityDetailFormatters";

export function FacilityCourtsPanel({
  courts,
}: {
  courts: FacilityRead<CourtResponse[]>;
}) {
  const rows = courts.data ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="facility-courts-heading"
      variant="outlined"
      sx={{ minWidth: 0, overflow: "hidden" }}
    >
      <Typography
        id="facility-courts-heading"
        component="h2"
        variant="h6"
        sx={{ fontWeight: 700, px: { xs: 2, md: 2.5 }, pt: { xs: 2, md: 2.5 } }}
      >
        Courts
      </Typography>
      {courts.state === "loading" ? (
        <Box
          role="status"
          aria-label="Loading courts"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 5 }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : courts.state === "error" ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={courts.onRetry}>
              Retry
            </Button>
          }
          sx={{ m: { xs: 2, md: 2.5 } }}
        >
          Courts could not be loaded. Try again.
        </Alert>
      ) : rows.length === 0 ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ p: { xs: 2, md: 2.5 } }}
        >
          No courts created yet.
        </Typography>
      ) : (
        <TableContainer
          aria-label="Facility courts"
          tabIndex={0}
          sx={{ mt: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Court</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((court) => (
                <TableRow key={court.court_id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {court.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={court.status} />
                  </TableCell>
                  <TableCell>{formatCourtTags(court.tags)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
